import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Wallet, Plus, Minus, Search, TrendingUp, TrendingDown, Users, ArrowUpRight, ArrowDownRight, RefreshCw } from "lucide-react";
import { StatsStrip } from "@/components/admin/stats-strip";
import { toast } from "sonner";
import { adminAdjustWallet } from "@/lib/wallets.functions";

export const Route = createFileRoute("/_authenticated/admin/wallets")({
  component: WalletsPage,
});

type WalletRow = {
  id: string;
  user_id: string;
  balance_cents: number;
  currency: string;
  updated_at: string;
  profile?: { display_name: string | null; avatar_url: string | null } | null;
};

type TxnRow = {
  id: string;
  wallet_id: string;
  user_id: string;
  type: string;
  amount_cents: number;
  balance_after_cents: number;
  currency: string;
  reason: string | null;
  created_at: string;
  created_by: string | null;
};

function fmt(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

function WalletsPage() {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [adjustFor, setAdjustFor] = useState<WalletRow | null>(null);

  const wallets = useQuery({
    queryKey: ["admin-wallets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wallets")
        .select("id,user_id,balance_cents,currency,updated_at")
        .order("balance_cents", { ascending: false })
        .limit(500);
      if (error) throw error;
      const ids = (data ?? []).map((w) => w.user_id);
      let profiles: Record<string, { display_name: string | null; avatar_url: string | null }> = {};
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id,display_name,avatar_url")
          .in("id", ids);
        profiles = Object.fromEntries((profs ?? []).map((p) => [p.id, p]));
      }
      return (data ?? []).map((w) => ({ ...w, profile: profiles[w.user_id] ?? null })) as WalletRow[];
    },
  });

  const txns = useQuery({
    queryKey: ["admin-wallet-txns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wallet_transactions")
        .select("id,wallet_id,user_id,type,amount_cents,balance_after_cents,currency,reason,created_at,created_by")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as TxnRow[];
    },
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = wallets.data ?? [];
    if (!q) return rows;
    return rows.filter(
      (w) =>
        w.user_id.toLowerCase().includes(q) ||
        (w.profile?.display_name ?? "").toLowerCase().includes(q),
    );
  }, [wallets.data, query]);

  const stats = useMemo(() => {
    const rows = wallets.data ?? [];
    const total = rows.reduce((s, r) => s + r.balance_cents, 0);
    const positive = rows.filter((r) => r.balance_cents > 0).length;
    const negative = rows.filter((r) => r.balance_cents < 0).length;
    const t = txns.data ?? [];
    const credits = t.filter((x) => x.amount_cents > 0).reduce((s, x) => s + x.amount_cents, 0);
    const debits = t.filter((x) => x.amount_cents < 0).reduce((s, x) => s + Math.abs(x.amount_cents), 0);
    return { count: rows.length, total, positive, negative, credits, debits };
  }, [wallets.data, txns.data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-wallets"] });
    qc.invalidateQueries({ queryKey: ["admin-wallet-txns"] });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Wallets" description="Per-user wallet balances and transaction history." />

      <StatsStrip
        tiles={[
          { label: "Wallets", value: stats.count, icon: Users, tone: "info" },
          { label: "Total balance", value: fmt(stats.total), icon: Wallet, tone: "success" },
          { label: "Funded users", value: stats.positive, icon: TrendingUp, tone: "success", hint: `${stats.negative} negative` },
          { label: "Credits (last 200)", value: fmt(stats.credits), icon: ArrowUpRight, tone: "success" },
          { label: "Debits (last 200)", value: fmt(stats.debits), icon: ArrowDownRight, tone: "warning" },
        ]}
      />

      <Tabs defaultValue="balances">
        <PillTabsList search>
          <PillTabsTrigger value="balances">Balances</PillTabsTrigger>
          <PillTabsTrigger value="ledger">Transactions</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="balances" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or user id…"
                className="pl-8"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Button size="sm" variant="outline" onClick={refresh} disabled={wallets.isFetching}>
              <RefreshCw className={`mr-1 h-4 w-4 ${wallets.isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <CreateWalletButton onCreated={refresh} />
          </div>

          {/* Desktop table */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Balance</th>
                    <th className="px-3 py-2">Updated</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((w) => (
                    <tr key={w.id} className="border-b transition-colors last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2">
                        <div className="font-medium">{w.profile?.display_name ?? "—"}</div>
                        <div className="font-mono text-xs text-muted-foreground">{w.user_id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-3 py-2 font-semibold tabular-nums">
                        <span className={w.balance_cents < 0 ? "text-destructive" : w.balance_cents > 0 ? "text-emerald-600" : ""}>
                          {fmt(w.balance_cents, w.currency)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(w.updated_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => setAdjustFor(w)}>
                          Adjust
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                        {wallets.isLoading ? "Loading…" : "No wallets yet. Use Adjust on a user to create one."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Mobile cards */}
          <div className="grid gap-2 md:hidden">
            {filtered.map((w) => (
              <Card key={w.id}>
                <CardContent className="flex items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{w.profile?.display_name ?? "—"}</div>
                    <div className="font-mono text-xs text-muted-foreground">{w.user_id.slice(0, 8)}…</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {new Date(w.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-semibold tabular-nums ${w.balance_cents < 0 ? "text-destructive" : w.balance_cents > 0 ? "text-emerald-600" : ""}`}>
                      {fmt(w.balance_cents, w.currency)}
                    </div>
                    <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => setAdjustFor(w)}>
                      Adjust
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  {wallets.isLoading ? "Loading…" : "No wallets yet."}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ledger">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Balance after</th>
                    <th className="px-3 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(txns.data ?? []).map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(t.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{t.user_id.slice(0, 8)}…</td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary">{t.type}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center gap-1 ${
                            t.amount_cents >= 0 ? "text-emerald-600" : "text-destructive"
                          }`}
                        >
                          {t.amount_cents >= 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          {fmt(t.amount_cents, t.currency)}
                        </span>
                      </td>
                      <td className="px-3 py-2">{fmt(t.balance_after_cents, t.currency)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{t.reason ?? "—"}</td>
                    </tr>
                  ))}
                  {(txns.data ?? []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                        {txns.isLoading ? "Loading…" : "No transactions yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AdjustDialog
        wallet={adjustFor}
        onClose={() => setAdjustFor(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["admin-wallets"] });
          qc.invalidateQueries({ queryKey: ["admin-wallet-txns"] });
        }}
      />
    </div>
  );
}


function CreateWalletButton({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const adjust = useServerFn(adminAdjustWallet);
  const m = useMutation({
    mutationFn: async () =>
      adjust({
        data: { user_id: userId.trim(), amount_cents: 0, type: "adjustment", reason: "Wallet initialized" },
      }),
    onSuccess: () => {
      toast.success("Wallet created");
      setOpen(false);
      setUserId("");
      onCreated();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="mr-1 h-4 w-4" /> Initialize wallet for user
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Initialize wallet</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>User ID</Label>
          <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="uuid" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={!userId || m.isPending}>
            {m.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({
  wallet,
  onClose,
  onSaved,
}: {
  wallet: WalletRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<"credit" | "debit">("credit");
  const [type, setType] = useState<string>("adjustment");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const adjust = useServerFn(adminAdjustWallet);

  const m = useMutation({
    mutationFn: async () => {
      if (!wallet) throw new Error("No wallet");
      const cents = Math.round(parseFloat(amount) * 100);
      if (!Number.isFinite(cents) || cents <= 0) throw new Error("Enter a positive amount");
      return adjust({
        data: {
          user_id: wallet.user_id,
          amount_cents: mode === "credit" ? cents : -cents,
          type: type as "credit" | "debit" | "adjustment" | "refund" | "purchase" | "payout",
          reason: reason.trim() || (mode === "credit" ? "Manual credit" : "Manual debit"),
        },
      });
    },
    onSuccess: () => {
      toast.success("Wallet updated");
      setAmount("");
      setReason("");
      onSaved();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={!!wallet} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust wallet</DialogTitle>
        </DialogHeader>
        {wallet && (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="text-muted-foreground">Current balance</div>
              <div className="text-lg font-semibold">{fmt(wallet.balance_cents, wallet.currency)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={mode === "credit" ? "default" : "outline"}
                onClick={() => setMode("credit")}
              >
                <Plus className="mr-1 h-4 w-4" /> Credit
              </Button>
              <Button
                type="button"
                variant={mode === "debit" ? "default" : "outline"}
                onClick={() => setMode("debit")}
              >
                <Minus className="mr-1 h-4 w-4" /> Debit
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Amount ({wallet.currency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="10.00"
              />
            </div>
            <div className="grid gap-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                  <SelectItem value="credit">Credit</SelectItem>
                  <SelectItem value="debit">Debit</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                  <SelectItem value="payout">Payout</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Reason</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this adjustment being made?"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending || !amount}>
            {m.isPending ? "Saving…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
